import React, { Suspense, useCallback } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFeature } from '@growthbook/growthbook-react';
import { useSetLayoutMode } from './hooks/useSetLayoutMode';
import { SCCOM_BRAND } from './constants/noNameBrands';
import * as ROUTE_TEMPLATES from './constants/routeTemplates';

import Loader from './components/Loader/Loader';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import ErrorBoundary from './components/Error/ErrorBoundary/ErrorBoundary';
import { usePrevious } from './hooks/useCompare';
import { useRouteTransition } from './hooks/useRouteTransition';
import { useRandomGameRoute } from './hooks/useRandomGameRoute';
import { useViewConfig } from './hooks/useViewConfig';
import { useIsFavouriteGamesEnabled } from './hooks/useIsFavouriteGamesEnabled';
import { brandSelector } from './redux/reducers/app/params';
import {
  getThemeDetailRoute,
  getCategoryDetailRoute,
  getProviderDetailRoute,
  getBonusesHistoryRoute,
  getEmptyBonusDetailRoute,
  getEmptyCampaignDetailRoute,
} from './utils/routes';
import { FEATURES } from './constants/featureToggling';

import {
  PARAM_THEME_ID,
  PARAM_CATEGORY_ID,
  PARAM_PROVIDER_ID,
  PARAM_PAGE_NUMBER,
} from './constants/routeTemplates';
import {
  HEADER_BRAND_CONFIG,
  DASHBOARD_LIST,
  ERROR_PAGE_COLLECTION,
  BONUS_GAMES_WAGERING,
} from './constants/viewConfig';
// Layouts
import TwoColumnLayout from './layouts/TwoColumnLayout/TwoColumnLayout';
import OneColumnLayout from './layouts/OneColumnLayout/OneColumnLayout';
// Pages
import RouterContextProvider from './components/Router/RouterContextProvider';
import { BONUSES_HISTORY_CATEGORY } from './AppSCCOM/hooks/useBonusCategories';

// dynamic pages
const RandomGamePage = React.lazy(() =>
  import('./pages/GamePage/RandomGamePage')
);
const SearchPage = React.lazy(() => import('./pages/SearchPage/SearchPage'));

const Categories = React.lazy(() => import('./pages/Categories/Categories'));
const CategoryDetails = React.lazy(() =>
  import('./pages/CategoryDetails/CategoryDetails')
);

const Providers = React.lazy(() => import('./pages/Providers/Providers'));
const ProviderDetails = React.lazy(() =>
  import('./pages/ProviderDetails/ProviderDetails')
);

const Themes = React.lazy(() => import('./pages/Themes/Themes'));
const ThemeDetails = React.lazy(() =>
  import('./pages/ThemeDetails/ThemeDetails')
);

const GamePage = React.lazy(() => import('./pages/GamePage/GamePage'));

const Favorites = React.lazy(() => import('./pages/Favorites/Favorites'));

const HallOfFameList = React.lazy(() =>
  import('./pages/HallOfFame/HallOfFame')
);

const CurrentBonuses = React.lazy(() =>
  import('./pages/CurrentBonuses/CurrentBonuses')
);
const BonusesHistory = React.lazy(() =>
  import('./pages/BonusesHistory/BonusesHistory')
);
const BonusDetails = React.lazy(() =>
  import('./pages/BonusDetails/BonusDetails')
);
const BonusGamesCollecting = React.lazy(() =>
  import('./pages/BonusGamesCollecting/BonusGamesCollecting')
);

// SCCOM

const CurrentBonusesSC = React.lazy(() =>
  import('./AppSCCOM/pages/CurrentBonuses/CurrentBonuses')
);
const BonusesHistorySC = React.lazy(() =>
  import('./AppSCCOM/pages/BonusesHistory/BonusesHistory')
);
const BonusDetailsSC = React.lazy(() =>
  import('./AppSCCOM/pages/BonusDetails/BonusDetails')
);
const BonusLayoutSC = React.lazy(() =>
  import('./AppSCCOM/components/BonusLayout/BonusLayout')
);
const RecentGamesSC = React.lazy(() =>
  import('./AppSCCOM/pages/RecentGames/RecentGames')
);
const SearchPageSC = React.lazy(() =>
  import('./AppSCCOM/pages/SearchPage/SearchPage')
);

export const PageErrorFallback = (error) => {
  return <Redirect to={{ pathname: '/500', state: { error: error.error } }} />;
};

const CustomBonusesRoutes = () => {
  useRouteTransition();

  const location = useLocation();
  const prevLocation = usePrevious(location);

  return (
    <Suspense fallback={<Loader />}>
      <Switch>
        <Route
          exact
          path={ROUTE_TEMPLATES.BONUSES_LIST}
          component={CurrentBonuses}
        />

        <PrivateRoute
          exact
          path={ROUTE_TEMPLATES.BONUSES_HISTORY}
          fallback={() => <Redirect to={ROUTE_TEMPLATES.BONUSES_LIST} />}
        >
          <BonusesHistory />
        </PrivateRoute>

        <Route
          exact
          path={[ROUTE_TEMPLATES.BONUS_DETAIL, ROUTE_TEMPLATES.CAMPAIGN_DETAIL]}
          component={(props) => (
            <>
              {prevLocation &&
              prevLocation.pathname === getBonusesHistoryRoute() ? (
                <BonusesHistory />
              ) : (
                <CurrentBonuses />
              )}
              <BonusDetails {...props} prevLocation={prevLocation} />
            </>
          )}
        />
      </Switch>
    </Suspense>
  );
};

const CustomBonusesRoutesSC = () => {
  useRouteTransition();

  const renderBonusesRoutes = useCallback(
    (category) => (
      <>
        {category.categoryId === BONUSES_HISTORY_CATEGORY ? (
          <PrivateRoute path="*">
            <BonusesHistorySC />
          </PrivateRoute>
        ) : (
          <CurrentBonusesSC currentCategory={category} />
        )}

        <Route
          exact
          path={[ROUTE_TEMPLATES.BONUS_DETAIL, ROUTE_TEMPLATES.CAMPAIGN_DETAIL]}
        >
          <Suspense fallback={''}>
            <BonusDetailsSC currentCategory={category} />
          </Suspense>
        </Route>
      </>
    ),
    []
  );

  return (
    <Suspense fallback={<Loader />}>
      <BonusLayoutSC>{renderBonusesRoutes}</BonusLayoutSC>
    </Suspense>
  );
};

const BonusesRoutes = () => {
  const brand = useSelector(brandSelector);

  return brand === SCCOM_BRAND ? (
    <CustomBonusesRoutesSC />
  ) : (
    <CustomBonusesRoutes />
  );
};

const Routes = () => {
  useRouteTransition();
  useSetLayoutMode();
  const randomGameRoute = useRandomGameRoute();

  const isEnabledFavoriteGames = useIsFavouriteGamesEnabled();

  const isEnabledThemes = useFeature(FEATURES.USE_THEMES).on;
  const Header = useViewConfig(HEADER_BRAND_CONFIG);
  const DashboardComponent = useViewConfig(DASHBOARD_LIST);
  const ErrorPage = useViewConfig(ERROR_PAGE_COLLECTION);
  const BonusGamesWagering = useViewConfig(BONUS_GAMES_WAGERING);
  const brand = useSelector(brandSelector);

  return (
    <RouterContextProvider>
      <Switch>
        {/* One column layout without Header */}
        <Route path={ROUTE_TEMPLATES.COMMON_ERROR}>
          <OneColumnLayout>
            <ErrorPage />
          </OneColumnLayout>
        </Route>

        {/* One column layout without Header. Game Page */}
        <Route
          path={[ROUTE_TEMPLATES.DEMO_GAME_DETAIL, ROUTE_TEMPLATES.GAME_DETAIL]}
        >
          <ErrorBoundary showDialog fallback={PageErrorFallback}>
            <OneColumnLayout>
              <Suspense fallback={<Loader />}>
                <Switch>
                  <Route exact path={randomGameRoute}>
                    <RandomGamePage />
                  </Route>

                  <Route path={ROUTE_TEMPLATES.GAME_DETAIL}>
                    <GamePage />
                  </Route>

                  <Route
                    exact
                    path={ROUTE_TEMPLATES.DEMO_GAME_DETAIL}
                    render={(props) => (
                      <Redirect
                        to={ROUTE_TEMPLATES.GAME_DETAIL.replace(
                          ':gameId',
                          props.match.params.gameId
                        )}
                      />
                    )}
                  />
                </Switch>
              </Suspense>
            </OneColumnLayout>
          </ErrorBoundary>
        </Route>

        {/* Two column with Header */}
        <Route
          exact
          path={[
            ROUTE_TEMPLATES.CATEGORY_LIST,
            ROUTE_TEMPLATES.CATEGORY_DETAIL,
            ROUTE_TEMPLATES.CATEGORY_DETAIL_PAGINATED,

            ROUTE_TEMPLATES.PROVIDER_LIST,
            ROUTE_TEMPLATES.PROVIDER_DETAIL,
            ROUTE_TEMPLATES.PROVIDER_DETAIL_PAGINATED,

            ROUTE_TEMPLATES.THEME_LIST,
            ROUTE_TEMPLATES.THEME_DETAIL,
            ROUTE_TEMPLATES.THEME_DETAIL_PAGINATED,

            ROUTE_TEMPLATES.BONUSES_LIST,
            ROUTE_TEMPLATES.BONUSES_HISTORY,
            ROUTE_TEMPLATES.BONUS_DETAIL,
            ROUTE_TEMPLATES.CAMPAIGN_DETAIL,

            ROUTE_TEMPLATES.HALL_OF_FAME,
            ROUTE_TEMPLATES.HALL_OF_FAME_HISTORY,
            ROUTE_TEMPLATES.BONUS_GAMES_WAGERING,
            ROUTE_TEMPLATES.BONUS_GAMES_COLLECTING,

            ROUTE_TEMPLATES.FAVORITES,
            ROUTE_TEMPLATES.RECENT_GAMES,
            ROUTE_TEMPLATES.SEARCH,
            ROUTE_TEMPLATES.ROOT,
          ]}
        >
          <ErrorBoundary showDialog fallback={PageErrorFallback}>
            <TwoColumnLayout header={<Header />}>
              <Suspense fallback={<Loader />}>
                <Switch>
                  <Route
                    path={ROUTE_TEMPLATES.CATEGORY_LIST}
                    exact
                    component={Categories}
                  />

                  <Route
                    path={[
                      ROUTE_TEMPLATES.CATEGORY_DETAIL,
                      ROUTE_TEMPLATES.CATEGORY_DETAIL_PAGINATED,
                    ]}
                  >
                    <Switch>
                      <Route
                        path={ROUTE_TEMPLATES.CATEGORY_DETAIL}
                        exact
                        component={CategoryDetails}
                      />

                      <Route
                        path={ROUTE_TEMPLATES.CATEGORY_DETAIL_PAGINATED}
                        render={(props) => {
                          const page = props.match.params[PARAM_PAGE_NUMBER];
                          const categoryId =
                            props.match.params[PARAM_CATEGORY_ID];

                          // eslint-disable-next-line
                          return page && page != 1 ? (
                            <CategoryDetails {...props} />
                          ) : (
                            <Redirect to={getCategoryDetailRoute(categoryId)} />
                          );
                        }}
                      />
                    </Switch>
                  </Route>

                  <Route
                    path={ROUTE_TEMPLATES.PROVIDER_LIST}
                    exact
                    component={Providers}
                  />

                  <Route
                    path={[
                      ROUTE_TEMPLATES.PROVIDER_DETAIL,
                      ROUTE_TEMPLATES.PROVIDER_DETAIL_PAGINATED,
                    ]}
                  >
                    <Switch>
                      <Route
                        path={ROUTE_TEMPLATES.PROVIDER_DETAIL}
                        exact
                        component={ProviderDetails}
                      />

                      <Route
                        path={ROUTE_TEMPLATES.PROVIDER_DETAIL_PAGINATED}
                        render={(props) => {
                          const page = props.match.params[PARAM_PAGE_NUMBER];
                          const providerId =
                            props.match.params[PARAM_PROVIDER_ID];

                          // eslint-disable-next-line
                          return page && page != 1 ? (
                            <ProviderDetails {...props} />
                          ) : (
                            <Redirect to={getProviderDetailRoute(providerId)} />
                          );
                        }}
                      />
                    </Switch>
                  </Route>

                  <Route
                    path={ROUTE_TEMPLATES.THEME_LIST}
                    exact
                    component={(...props) =>
                      isEnabledThemes ? (
                        <Themes {...props} />
                      ) : (
                        <Redirect to={ROUTE_TEMPLATES.ROOT} />
                      )
                    }
                  />

                  <Route
                    path={[
                      ROUTE_TEMPLATES.THEME_DETAIL,
                      ROUTE_TEMPLATES.THEME_DETAIL_PAGINATED,
                    ]}
                  >
                    <Switch>
                      <Route
                        path={ROUTE_TEMPLATES.THEME_DETAIL}
                        exact
                        component={(...props) =>
                          isEnabledThemes ? (
                            <ThemeDetails {...props} />
                          ) : (
                            <Redirect to={ROUTE_TEMPLATES.ROOT} />
                          )
                        }
                      />

                      <Route
                        path={ROUTE_TEMPLATES.THEME_DETAIL_PAGINATED}
                        render={(props) => {
                          if (!isEnabledThemes) {
                            return <Redirect to={ROUTE_TEMPLATES.ROOT} />;
                          }

                          const page = props.match.params[PARAM_PAGE_NUMBER];
                          const themeId = props.match.params[PARAM_THEME_ID];

                          // eslint-disable-next-line
                          return page && page != 1 ? (
                            <ThemeDetails {...props} />
                          ) : (
                            <Redirect to={getThemeDetailRoute(themeId)} />
                          );
                        }}
                      />
                    </Switch>
                  </Route>

                  <Route
                    path={[
                      ROUTE_TEMPLATES.BONUSES_LIST,
                      ROUTE_TEMPLATES.BONUSES_HISTORY,
                      ROUTE_TEMPLATES.BONUS_DETAIL,
                      ROUTE_TEMPLATES.CAMPAIGN_DETAIL,
                    ]}
                    exact
                    component={BonusesRoutes}
                  />

                  <Route
                    exact
                    path={[
                      ROUTE_TEMPLATES.HALL_OF_FAME,
                      ROUTE_TEMPLATES.HALL_OF_FAME_HISTORY,
                    ]}
                    component={HallOfFameList}
                  />

                  <Route
                    path={ROUTE_TEMPLATES.BONUS_GAMES_WAGERING}
                    exact
                    component={BonusGamesWagering}
                  />

                  <Route
                    path={ROUTE_TEMPLATES.BONUS_GAMES_COLLECTING}
                    exact
                    component={BonusGamesCollecting}
                  />

                  <Route path={ROUTE_TEMPLATES.SEARCH} exact>
                    {brand === SCCOM_BRAND ? <SearchPageSC /> : <SearchPage />}
                  </Route>

                  <PrivateRoute
                    path={ROUTE_TEMPLATES.FAVORITES}
                    exact
                    fallback={() => <Redirect to={ROUTE_TEMPLATES.ROOT} />}
                  >
                    {isEnabledFavoriteGames ? (
                      <Favorites />
                    ) : (
                      <Redirect to={ROUTE_TEMPLATES.ROOT} />
                    )}
                  </PrivateRoute>
                  <PrivateRoute
                    path={ROUTE_TEMPLATES.RECENT_GAMES}
                    exact
                    fallback={() => <Redirect to={ROUTE_TEMPLATES.NO_MATCH} />}
                  >
                    {brand === SCCOM_BRAND ? (
                      <RecentGamesSC />
                    ) : (
                      <Redirect to={ROUTE_TEMPLATES.NO_MATCH} />
                    )}
                  </PrivateRoute>

                  <Route
                    path={ROUTE_TEMPLATES.ROOT}
                    exact
                    component={DashboardComponent}
                  />
                </Switch>
              </Suspense>
            </TwoColumnLayout>
          </ErrorBoundary>
        </Route>

        {/* Empty routes with redirect to Bonuses list */}
        <Route
          exact
          path={[getEmptyBonusDetailRoute(), getEmptyCampaignDetailRoute()]}
        >
          <Redirect to={{ pathname: ROUTE_TEMPLATES.BONUSES_LIST }} />
        </Route>

        <Route
          path={ROUTE_TEMPLATES.OLD_BONUS_GAMES}
          exact
          render={() => <Redirect to={ROUTE_TEMPLATES.BONUS_GAMES_WAGERING} />}
        />

        <Route
          exact
          path={ROUTE_TEMPLATES.OLD_BONUS_DETAIL}
          render={(props) => (
            <Redirect
              to={ROUTE_TEMPLATES.BONUS_DETAIL.replace(
                ':bonusId',
                props.match.params.bonusId
              )}
            />
          )}
        />

        <Route
          exact
          path={ROUTE_TEMPLATES.OLD_CAMPAIGN_DETAIL}
          render={(props) => (
            <Redirect
              to={ROUTE_TEMPLATES.CAMPAIGN_DETAIL.replace(
                ':campaignId',
                props.match.params.campaignId
              )}
            />
          )}
        />

        {/* Not Found */}
        <Route path={ROUTE_TEMPLATES.NO_MATCH}>
          <OneColumnLayout>
            <ErrorPage status={404} />
          </OneColumnLayout>
        </Route>
      </Switch>
    </RouterContextProvider>
  );
};

export default Routes;
